import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { Award, Plus } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { Tabs } from '../../../../ui';
import { getInterviewRounds } from '../../../../utils/api_service/JobOpenings';
import {
  Candidates,
  Configurations,
  Offer,
  Overview,
  Questions,
  Results,
} from './Containers';
import { InviteTemplate } from './Containers/InvitationTemplates/InviteTemplateCandidate';

export const Interviews: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  const [interviewRounds, setInterviewRounds] = useState<any[]>([]);
  const [currentRound, setCurrentRound] = useState<string>('');
  const [isLoadingRounds, setIsLoadingRounds] = useState<boolean>(true);

  // Get interview tab from URL
  const getInterviewTabFromUrl = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('interviewTab');
    const validTabs = [
      'configuration',
      'overview',
      'invitationTemplate',
      'candidates',
      'questions',
      'results',
      'offer',
    ];
    return validTabs.includes(tab || '') ? tab || 'configuration' : 'configuration';
  };

  // Get round from URL
  const getRoundFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('round');
  };

  const [activeTab, setActiveTab] = useState<string>(getInterviewTabFromUrl());

  const numberWords = [
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
  ];
  const getRoundLabel = (index: number) => `Round ${numberWords[index] || index + 1}`;

  // Update URL when interview tab changes
  const updateActiveTab = (tab: string) => {
    setActiveTab(tab);
    const searchParams = new URLSearchParams();
    searchParams.set('tab', 'Smart Interviews');
    searchParams.set('interviewTab', tab);

    if (currentRound) {
      searchParams.set('round', currentRound);
    }

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  // Sync state with URL on location change
  useEffect(() => {
    const tabFromUrl = getInterviewTabFromUrl();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  const fetchInterviewRounds = useCallback(async () => {
    setIsLoadingRounds(true);
    try {
      const response = await getInterviewRounds(id);
      const data = await response.json();
      const sortedRounds = data.sort((a: any, b: any) => {
        const aIndex = a.title?.match(/Round (\d+)/i)?.[1] || 0;
        const bIndex = b.title?.match(/Round (\d+)/i)?.[1] || 0;
        return Number(aIndex) - Number(bIndex);
      });

      setInterviewRounds(sortedRounds);

      const urlTab = getInterviewTabFromUrl();
      const urlRound = getRoundFromUrl();
      const hasUrlTab = new URLSearchParams(location.search).has('interviewTab');
      const hasUrlRound = new URLSearchParams(location.search).has('round');

      if (!hasUrlTab && !hasUrlRound) {
        if (sortedRounds.length > 0) {
          setCurrentRound(getRoundLabel(0));
          updateActiveTab('overview');
        } else {
          setCurrentRound('');
          updateActiveTab('configuration');
        }
      } else {
        if (hasUrlRound && urlRound) {
          const roundExists =
            sortedRounds.length > 0 &&
            sortedRounds.some((_: any, idx: number) => getRoundLabel(idx) === urlRound);

          if (roundExists) {
            setCurrentRound(urlRound);
          } else if (sortedRounds.length > 0) {
            setCurrentRound(getRoundLabel(0));
          } else {
            setCurrentRound('');
          }
        } else if (sortedRounds.length > 0) {
          setCurrentRound(getRoundLabel(0));
        } else {
          setCurrentRound('');
        }

        setActiveTab(urlTab);
      }
    } catch {
      setInterviewRounds([]);
    } finally {
      setIsLoadingRounds(false);
    }
  }, [id, location.search]);

  useEffect(() => {
    fetchInterviewRounds();
  }, [fetchInterviewRounds]);

  const buttonsList = useMemo(
    () =>
      interviewRounds.map((_, idx) => ({
        label: getRoundLabel(idx),
      })),
    [interviewRounds],
  );

  const interviewRenderMap = useMemo(() => {
    const map: Record<string, React.ReactNode> = {};
    interviewRounds.forEach((round, idx) => {
      const label = getRoundLabel(idx);
      const isLastRound = idx === interviewRounds.length - 1;

      map[label] = (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 2.5 }}>
            <Tabs
              tabs={[
                { value: 'overview', label: t(SI.tabs.overview) },
                { value: 'configuration', label: t(SI.tabs.configuration) },
                { value: 'invitationTemplate', label: t(SI.tabs.invitationTemplate) },
                { value: 'candidates', label: t(SI.tabs.candidates) },
                { value: 'results', label: t(SI.tabs.results) },
                ...(isLastRound ? [{ value: 'offer', label: t(SI.tabs.offer) }] : []),
              ]}
              activeTab={activeTab}
              onTabChange={updateActiveTab}
            />
          </Box>

          <Box sx={{ py: 1 }}>
            {!round.id ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <>
                {activeTab === 'overview' && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.overview)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Overview interviewRoundId={round.id} />
                  </React.Suspense>
                )}
                {activeTab === 'configuration' && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.configuration)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Configurations
                      jobOpeningId={id!}
                      interviewId={round.id}
                      onRoundCreated={fetchInterviewRounds}
                    />
                  </React.Suspense>
                )}
                {activeTab === 'invitationTemplate' && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.invitationTemplate)}
                        </Typography>
                      </Box>
                    }
                  >
                    <InviteTemplate interviewId={round.id} />
                  </React.Suspense>
                )}
                {activeTab === 'candidates' && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.candidates)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Candidates
                      interviewRoundID={round.id}
                      jobOpeningId={id!}
                      allRounds={interviewRounds}
                      currentRoundIndex={interviewRounds.findIndex((r: any) => r.id === round.id)}
                    />
                  </React.Suspense>
                )}
                {activeTab === 'questions' && <Questions />}
                {activeTab === 'results' && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.results)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Results interviewRoundId={round.id} />
                  </React.Suspense>
                )}
                {activeTab === 'offer' && idx === interviewRounds.length - 1 && (
                  <React.Suspense
                    fallback={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t(SI.loading.offer)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Offer jobOpeningId={id!} />
                  </React.Suspense>
                )}
              </>
            )}
          </Box>
        </Box>
      );
    });
    return map;
  }, [interviewRounds, activeTab, id, t, SI]);

  const renderFormPage = () => {
    if (isLoadingRounds) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '380px', gap: 2 }}>
          <CircularProgress size={36} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {t(SI.loading.rounds)}
          </Typography>
        </Box>
      );
    }

    if (!currentRound) {
      return (
        <Box sx={{ width: '100%' }}>
          <React.Suspense
            fallback={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t(SI.loading.configuration)}
                </Typography>
              </Box>
            }
          >
            <Configurations
              jobOpeningId={id!}
              interviewId=""
              onRoundCreated={fetchInterviewRounds}
            />
          </React.Suspense>
        </Box>
      );
    }

    return (
      interviewRenderMap[currentRound] || (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(SI.noContent)}
          </Typography>
        </Box>
      )
    );
  };

  const isRoundActive = (roundLabel: string) => {
    return currentRound === roundLabel && activeTab !== 'offer';
  };

  const isOfferActive = () => {
    return (
      currentRound === getRoundLabel(interviewRounds.length - 1) &&
      activeTab === 'offer'
    );
  };

  const isNewRoundActive = currentRound === '' && activeTab === 'configuration';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 2.5,
        mt: 2,
        width: '100%',
      }}
    >
      {/* Left Vertical Round Navigation Rail */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', lg: 'column' },
          gap: 1.5,
          p: 1.5,
          borderRadius: { xs: '16px', lg: '20px' },
          bgcolor: alpha(theme.palette.action.hover, 0.04),
          border: '1px solid',
          borderColor: 'divider',
          overflowX: { xs: 'auto', lg: 'visible' },
          width: { xs: '100%', lg: 68 },
          flexShrink: 0,
          position: { lg: 'sticky' },
          top: { lg: 16 },
          height: { lg: 'fit-content' },
          alignItems: 'center',
        }}
      >
        {/* New Round Action Button */}
        <Tooltip title={t(SI.newRound)} placement="right">
          <ButtonBase
            onClick={() => {
              setCurrentRound('');
              updateActiveTab('configuration');
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderRadius: '14px',
              p: 0.75,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: isNewRoundActive ? 'primary.main' : 'divider',
                bgcolor: isNewRoundActive ? 'primary.main' : 'background.paper',
                color: isNewRoundActive ? '#ffffff' : 'text.secondary',
                boxShadow: isNewRoundActive
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.35)}`
                  : '0 1px 3px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: isNewRoundActive ? '#ffffff' : 'primary.main',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Plus size={18} strokeWidth={2.5} />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.625rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                color: isNewRoundActive ? 'primary.main' : 'text.secondary',
                lineHeight: 1.1,
              }}
            >
              {t(SI.newRound)}
            </Typography>
          </ButtonBase>
        </Tooltip>

        <Divider
          orientation="horizontal"
          flexItem
          sx={{
            display: { xs: 'none', lg: 'block' },
            my: 0.5,
            borderColor: 'divider',
          }}
        />

        {/* Round Number Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', lg: 'column' },
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {buttonsList.map(({ label }, idx) => {
            const isActive = isRoundActive(label);
            return (
              <Tooltip key={label} title={label} placement="right">
                <ButtonBase
                  onClick={() => {
                    setCurrentRound(label);
                    const searchParams = new URLSearchParams();
                    searchParams.set('tab', 'Smart Interviews');
                    searchParams.set('interviewTab', 'overview');
                    searchParams.set('round', label);
                    const newUrl = `${location.pathname}?${searchParams.toString()}`;
                    window.history.pushState(null, '', newUrl);
                    setActiveTab('overview');
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    borderRadius: '14px',
                    p: 0.75,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      border: '1px solid',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      bgcolor: isActive ? 'primary.main' : 'background.paper',
                      color: isActive ? '#ffffff' : 'text.primary',
                      boxShadow: isActive
                        ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.35)}`
                        : '0 1px 3px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: isActive ? '#ffffff' : 'primary.main',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      lineHeight: 1.1,
                    }}
                  >
                    {label.split(' ')[1]}
                  </Typography>
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>

        {/* Offer Action Button */}
        {interviewRounds.length > 0 && (
          <>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                display: { xs: 'none', lg: 'block' },
                my: 0.5,
                borderColor: 'divider',
              }}
            />
            <Tooltip title={t(SI.tabs.offer)} placement="right">
              <ButtonBase
                onClick={() => {
                  const lastRoundLabel = getRoundLabel(interviewRounds.length - 1);
                  setCurrentRound(lastRoundLabel);
                  const searchParams = new URLSearchParams();
                  searchParams.set('tab', 'Smart Interviews');
                  searchParams.set('interviewTab', 'offer');
                  searchParams.set('round', lastRoundLabel);
                  const newUrl = `${location.pathname}?${searchParams.toString()}`;
                  window.history.pushState(null, '', newUrl);
                  setActiveTab('offer');
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: '14px',
                  p: 0.75,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: isOfferActive() ? 'primary.main' : 'divider',
                    bgcolor: isOfferActive() ? 'primary.main' : 'background.paper',
                    color: isOfferActive() ? '#ffffff' : 'text.secondary',
                    boxShadow: isOfferActive()
                      ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.35)}`
                      : '0 1px 3px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: isOfferActive() ? '#ffffff' : 'primary.main',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Award size={18} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    color: isOfferActive() ? 'primary.main' : 'text.secondary',
                    lineHeight: 1.1,
                  }}
                >
                  {t(SI.tabs.offer)}
                </Typography>
              </ButtonBase>
            </Tooltip>
          </>
        )}
      </Paper>

      {/* Main Content Area */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: '20px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.03)',
          minHeight: '400px',
        }}
      >
        {renderFormPage()}
      </Paper>
    </Box>
  );
};

